import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const DEFAULT_TABLE_PAGE_SIZE = 50;

interface TablePaginationProps {
  totalItems: number;
  page: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

export function usePagination<T>(items: T[], page: number, pageSize = DEFAULT_TABLE_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paginatedItems = items.slice(safePage * pageSize, (safePage + 1) * pageSize);
  return { paginatedItems, totalPages, safePage };
}

export function TablePagination({
  totalItems,
  page,
  onPageChange,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  pageSizeOptions = [25, 50, 100, 200],
  onPageSizeChange,
  className,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const showPager = totalPages > 1 || !!onPageSizeChange;

  if (!showPager && totalItems === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-border text-sm text-muted-foreground",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span>
          {totalItems === 0
            ? "0 results"
            : `${safePage * pageSize + 1}–${Math.min((safePage + 1) * pageSize, totalItems)} of ${totalItems}`}
        </span>
        {onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-xs whitespace-nowrap">Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-8 w-[4.5rem] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>
      {totalPages > 1 ? (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage === 0}
            onClick={() => onPageChange(safePage - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-2 text-foreground text-xs font-medium">
            {safePage + 1} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage >= totalPages - 1}
            onClick={() => onPageChange(safePage + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
